import React, { useState, useEffect } from "react";
import { TYPE_PROFILE, MAP_TILE_ORDER_INDEX } from "../../utils/typeProfile";
import { apiJsonOrThrow, setTransientMessage } from "./adminUtils";
import LoadingButton from "../common/LoadingButton";
import LoadingIndicator from "../common/LoadingIndicator";

const POLICY_OPTIONS = [
  { value: "create", label: "Create" },
  { value: "map_to_existing", label: "Map to existing" },
  { value: "discard", label: "Discard" },
];

const FALLBACK_OPTIONS = [
  { value: "discard", label: "Discard" },
  { value: "create", label: "Create" },
];

const EntityResolutionPolicyManagement = ({
  user,
  hideHeader = false,
  mapLanguage = false,
}) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const loadPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiJsonOrThrow("/api/admin/entity-resolution-policies");
      const list = (data.data || [])
        .map((row) => ({
          entity_type: row.entity_type,
          policy: row.policy || "create",
          map_fallback: row.map_fallback || null,
          similarity_threshold: row.similarity_threshold ?? null,
          use_llm_for_mapping: !!row.use_llm_for_mapping,
          llm_confidence_threshold: row.llm_confidence_threshold ?? null,
          updatable_standard_attributes: row.updatable_standard_attributes || [],
          updatable_custom_attributes: row.updatable_custom_attributes || [],
        }))
        .sort(
          (a, b) =>
            (MAP_TILE_ORDER_INDEX[a.entity_type] ?? 999) -
              (MAP_TILE_ORDER_INDEX[b.entity_type] ?? 999) ||
            String(a.entity_type).localeCompare(String(b.entity_type))
        );
      setPolicies(list);
    } catch (err) {
      setError(err.message || "Failed to load policies");
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicies();
  }, []);

  const handlePolicyChange = (entityType, field, value) => {
    setPolicies((prev) =>
      prev.map((p) =>
        p.entity_type === entityType ? { ...p, [field]: value } : p
      )
    );
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      setSuccessMessage("");
      await apiJsonOrThrow("/api/admin/entity-resolution-policies", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ policies }),
      });
      setTransientMessage(setSuccessMessage, "Match or create rules saved.", 3000);
    } catch (err) {
      setError(err.message || "Failed to save policies");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeLabel = (entityType) => {
    const profile = TYPE_PROFILE[entityType];
    return profile ? profile.label : entityType;
  };

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="max-w-6xl">
          <LoadingIndicator
            label="Loading match or create rules..."
            size="md"
            invert
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4">
      <div className="max-w-6xl">
        {/* Header */}
        {!hideHeader ? (
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-white mb-2">
              {mapLanguage ? "Match or create" : "Configure Entity Resolution Policy"}
            </h1>
            <p className="text-gray-400">
              {mapLanguage ? (
                <>
                  When the AI builds your map from documents or imports, decide what
                  happens for each item type:{" "}
                  <strong className="text-gray-300">Create</strong> (add as new),{" "}
                  <strong className="text-gray-300">Map to existing</strong> (link to the
                  closest match), or <strong className="text-gray-300">Discard</strong>. For
                  Map to existing, set a fallback when nothing matches. Turn on AI mapping to
                  compare names and descriptions with items already on your map.
                </>
              ) : (
                <>
                  Control how extracted entities are resolved at import:{" "}
                  <strong className="text-gray-300">Create</strong> (new entity),{" "}
                  <strong className="text-gray-300">Map to existing</strong> (link to closest
                  same-type entity), or <strong className="text-gray-300">Discard</strong>. For
                  Map to existing, choose what to do when no match is found. Enable &quot;Use
                  LLM for mapping&quot; to let the AI compare names and descriptions with
                  existing entities and pick the best match.
                </>
              )}
            </p>
          </div>
        ) : null}

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-600/20 border border-green-600 rounded-lg text-green-300">
            {successMessage}
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-600/20 border border-red-600 rounded-lg text-red-300">
            {error}
          </div>
        )}

        {/* Policy cards */}
        <div className="mb-6 space-y-3">
          {policies.map((row) => (
            <div key={row.entity_type} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-sm font-semibold text-white">{getTypeLabel(row.entity_type)}</p>
                <span className="text-xs text-gray-400">{row.entity_type}</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <label className="text-xs text-gray-300">
                  <span className="block mb-1">Policy</span>
                  <select
                    value={row.policy}
                    onChange={(e) => handlePolicyChange(row.entity_type, "policy", e.target.value)}
                    className="w-full rounded border border-gray-600 bg-gray-700 text-white text-sm px-2 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {POLICY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-xs text-gray-300">
                  <span className="block mb-1">Map fallback</span>
                  {row.policy === "map_to_existing" ? (
                    <select
                      value={row.map_fallback || ""}
                      onChange={(e) =>
                        handlePolicyChange(row.entity_type, "map_fallback", e.target.value || null)
                      }
                      className="w-full rounded border border-gray-600 bg-gray-700 text-white text-sm px-2 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">—</option>
                      {FALLBACK_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="h-[34px] rounded border border-gray-700 bg-gray-800 text-gray-500 px-2 flex items-center">—</div>
                  )}
                </label>

                <label className="text-xs text-gray-300">
                  <span className="block mb-1">Similarity threshold</span>
                  {row.policy === "map_to_existing" ? (
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.05"
                      placeholder="0.85"
                      value={row.similarity_threshold ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        handlePolicyChange(
                          row.entity_type,
                          "similarity_threshold",
                          v === "" ? null : parseFloat(v)
                        );
                      }}
                      className="w-full rounded border border-gray-600 bg-gray-700 text-white text-sm px-2 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500"
                    />
                  ) : (
                    <div className="h-[34px] rounded border border-gray-700 bg-gray-800 text-gray-500 px-2 flex items-center">—</div>
                  )}
                </label>

                <label className="text-xs text-gray-300">
                  <span className="block mb-1">Use LLM for mapping</span>
                  <div className="h-[34px] rounded border border-gray-700 bg-gray-800 px-2 flex items-center">
                    {row.policy === "map_to_existing" ? (
                      <input
                        type="checkbox"
                        checked={row.use_llm_for_mapping}
                        onChange={(e) =>
                          handlePolicyChange(row.entity_type, "use_llm_for_mapping", e.target.checked)
                        }
                        className="rounded border-gray-600 bg-gray-700 text-primary-600 focus:ring-primary-500"
                      />
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </div>
                </label>

                <label className="text-xs text-gray-300">
                  <span className="block mb-1">LLM confidence</span>
                  {row.policy === "map_to_existing" && row.use_llm_for_mapping ? (
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.05"
                      placeholder="0.85"
                      value={row.llm_confidence_threshold ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        handlePolicyChange(
                          row.entity_type,
                          "llm_confidence_threshold",
                          v === "" ? null : parseFloat(v)
                        );
                      }}
                      className="w-full rounded border border-gray-600 bg-gray-700 text-white text-sm px-2 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500"
                    />
                  ) : (
                    <div className="h-[34px] rounded border border-gray-700 bg-gray-800 text-gray-500 px-2 flex items-center">—</div>
                  )}
                </label>

                <label className="text-xs text-gray-300">
                  <span className="block mb-1">Update description</span>
                  <div className="h-[34px] rounded border border-gray-700 bg-gray-800 px-2 flex items-center">
                    {row.policy === "map_to_existing" ? (
                      <input
                        type="checkbox"
                        checked={Array.isArray(row.updatable_standard_attributes)
                          ? row.updatable_standard_attributes.includes("description")
                          : false}
                        onChange={(e) => {
                          const allowed = Array.isArray(row.updatable_standard_attributes)
                            ? [...row.updatable_standard_attributes]
                            : [];
                          if (e.target.checked) {
                            if (!allowed.includes("description")) {
                              allowed.push("description");
                            }
                          } else {
                            const idx = allowed.indexOf("description");
                            if (idx !== -1) {
                              allowed.splice(idx, 1);
                            }
                          }
                          handlePolicyChange(
                            row.entity_type,
                            "updatable_standard_attributes",
                            allowed
                          );
                        }}
                        className="rounded border-gray-600 bg-gray-700 text-primary-600 focus:ring-primary-500"
                      />
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </div>
                </label>
              </div>

              <div className="mt-3">
                <label className="text-xs text-gray-300 block mb-1">
                  Updatable custom attributes
                </label>
                {row.policy === "map_to_existing" ? (
                  <input
                    type="text"
                    value={
                      Array.isArray(row.updatable_custom_attributes)
                        ? row.updatable_custom_attributes.join(", ")
                        : ""
                    }
                    onChange={(e) => {
                      const raw = e.target.value || "";
                      const parts = raw
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean);
                      handlePolicyChange(
                        row.entity_type,
                        "updatable_custom_attributes",
                        parts
                      );
                    }}
                    placeholder="e.g. cost_center, owner, support_model"
                    className="w-full rounded border border-gray-600 bg-gray-700 text-white text-xs px-2 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500"
                  />
                ) : (
                  <div className="h-[34px] rounded border border-gray-700 bg-gray-800 text-gray-500 px-2 flex items-center">
                    —
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <LoadingButton
            type="button"
            onClick={handleSave}
            loading={isSubmitting}
            loadingLabel="Saving..."
            className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-900"
          >
            Save
          </LoadingButton>
        </div>
      </div>
    </div>
  );
};

export default EntityResolutionPolicyManagement;
